import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { Dialog, Toast } from "antd-mobile";
import { toastSuccess, toastWarning } from "@/utils/toast";
import { getMyImages, updateMyAlbums } from "@/api/staff";
import { uploadImage } from "@/api/upload";

const BRAND = "#FF6B35";
const MAX_ALBUM = 9;

export default function AlbumPage() {
  const navigate = useNavigate();
  const [images, setImages] = useState<string[]>([]);
  const [batchMode, setBatchMode] = useState(false);
  const [batchSelectIdxs, setBatchSelectIdxs] = useState<number[]>([]);
  const [allCheck, setAllCheck] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 全选计算属性等效
  useEffect(() => {
    if (batchMode && images.length > 0) {
      setAllCheck(batchSelectIdxs.length === images.length);
    }
  }, [batchSelectIdxs, batchMode, images.length]);

  // 全选变化
  useEffect(() => {
    if (allCheck && batchMode) {
      setBatchSelectIdxs(images.map((_, i) => i));
    }
  }, [allCheck]);

  // 获取相册图片
  const fetchAlbums = async () => {
    try {
      console.log("[Album] 正在获取相册图片...");
      const raw = await getMyImages();
      console.log("[Album] 接口原始返回:", raw);

      let list: string[] = [];
      if (Array.isArray(raw)) {
        list = raw;
      } else if (raw && typeof raw === "object" && "data" in raw) {
        const d = (raw as any).data;
        list = Array.isArray(d) ? d : [];
      } else if (typeof raw === "string" && raw) {
        try {
          list = JSON.parse(raw);
        } catch {
          /* ignore */
        }
      }

      console.log("[Album] 解析后图片列表:", list, "共", list.length, "张");
      setImages(list);
    } catch (e: any) {
      console.error("[Album] 获取图片失败:", e);
      toastWarning(e?.message || "获取相册失败");
    }
  };

  useEffect(() => {
    fetchAlbums();
  }, []);

  // ── 切换批量管理模式 ──
  const toggleBatchMode = async () => {
    if (!batchMode) {
      await fetchAlbums(); // 进入前刷新
      setBatchMode(true);
      setBatchSelectIdxs([]);
      setAllCheck(false);
    } else {
      setBatchMode(false);
      setBatchSelectIdxs([]);
      setAllCheck(false);
    }
  };

  // 单项选择
  const handleSelectChange = (index: number) => {
    setBatchSelectIdxs((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    );
  };

  // 全选/反选
  const toggleAllCheck = () => {
    if (batchSelectIdxs.length === images.length) {
      setBatchSelectIdxs([]);
      setAllCheck(false);
    } else {
      setBatchSelectIdxs(images.map((_, i) => i));
      setAllCheck(true);
    }
  };

  // 取消批量选择
  const cancelBatchSelect = () => {
    toggleBatchMode();
  };

  // ── 批量删除 ──
  const handleBatchDelete = () => {
    if (batchSelectIdxs.length === 0) return toastWarning("请选择要删除的图片");

    Dialog.show({
      content: `确认删除选中的 ${batchSelectIdxs.length} 张图片？`,
      closeOnAction: true,
      actions: [
        [
          {
            key: "cancel",
            text: "取消",
            style: { color: "#FF6B35", fontWeight: 500 },
            onClick: () => {},
          },
          {
            key: "confirm",
            text: "确认删除",
            bold: true,
            style: { color: "#FF6B35", fontWeight: 600 },
            onClick: async () => {
              const sorted = [...batchSelectIdxs].sort((a, b) => b - a);
              const nextImages = [...images];
              sorted.forEach((idx) => nextImages.splice(idx, 1));
              try {
                Toast.show({ icon: "loading", content: "删除中..." });
                await updateMyAlbums({ imgs: nextImages });
                setImages(nextImages);
                toastSuccess("删除成功");
                toggleBatchMode();
              } catch (err: any) {
                toastWarning(err?.message || "删除失败");
              }
            },
          },
        ],
      ],
    });
  };

  // ── 上传图片处理（ref 触发）──
  const handleFileChange = async () => {
    // 必须用 ref.current.files 读取，不能用 e.target（React 合成事件会回收 e.target，导致 files 为空）
    const inputEl = fileInputRef.current;
    if (!inputEl || !inputEl.files || inputEl.files.length === 0) return;

    const files = Array.from(inputEl.files);
    console.log("[Album] 选择文件:", files);

    // 重置 input 以支持重复选同一文件
    inputEl.value = "";

    const remain = MAX_ALBUM - images.length;
    if (remain <= 0) {
      toastWarning(`相册最多 ${MAX_ALBUM} 张图片`);
      return;
    }

    setUploading(true);
    try {
      Toast.show({
        icon: "loading",
        content: "上传中...",
        maskClickable: false,
      });

      const fileList = files.slice(0, remain);
      console.log(
        "[Album] 选择文件数:",
        files.length,
        "实际上传:",
        fileList.length,
        "剩余可传:",
        remain,
      );

      // 逐张上传
      const results: string[] = [];
      for (let i = 0; i < fileList.length; i++) {
        try {
          console.log(
            `[Album] 开始上传第 ${i + 1}/${fileList.length} 张:`,
            fileList[i].name,
            fileList[i].size,
          );
          const url = await uploadImage(fileList[i]);
          console.log(`[Album] 第 ${i + 1} 张上传完成, URL:`, url);
          if (url) results.push(url);
        } catch (err) {
          console.error(`[Album] 第 ${i + 1} 张上传失败:`, err);
        }
      }

      console.log("[Album] 上传结果:", results);
      // 上传到 api 项目获取 URL，再保存到 staffapi
      const nextImages = [...images, ...results];
      await updateMyAlbums({ imgs: nextImages });
      setImages(nextImages);
      toastSuccess("上传成功");
    } catch (err: any) {
      console.error("[Album] 上传失败:", err);
      toastWarning(err?.message || "上传失败");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 隐藏文件 input — 与 ImageUploader 组件一致 */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        disabled={uploading}
        onChange={handleFileChange}
        className="sr-only"
        aria-hidden
        tabIndex={-1}
      />

      {/* Header */}
      <div style={{ background: BRAND }}>
        <div
          className="px-5 flex items-center justify-between"
          style={{
            paddingTop: "calc(env(safe-area-inset-top,0px) + 16px)",
            paddingBottom: 13,
          }}
        >
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="w-9 h-9 flex items-center justify-center rounded-full cursor-pointer active:scale-[0.98] shrink-0"
              style={{ background: "rgba(255,255,255,0.2)" }}
            >
              <i
                className="ri-arrow-left-line"
                style={{ fontSize: 20, color: "#fff" }}
              />
            </button>
            <span className="text-lg font-bold text-white">我的相册</span>
          </div>
          {images.length > 0 && (
            <button
              onClick={toggleBatchMode}
              className={`w-[100px] h-[35px] rounded-full text-xs font-medium m-0 cursor-pointer active:scale-[0.98] transition-all`}
              style={
                batchMode
                  ? { background: "#fff", color: BRAND, border: "none" }
                  : {
                      color: "#fff",
                      background: "transparent",
                      border: "1px solid rgba(255,255,255,0.7)",
                    }
              }
            >
              {batchMode ? "退出管理" : "批量管理"}
            </button>
          )}
        </div>
      </div>

      <div
        className="px-4 pt-4 pb-24 overflow-y-auto"
        style={{ height: "calc(100vh - 56px - 80px)" }}
      >
        {/* 图片列表 — 3列网格 */}
        {images.length > 0 && (
          <div className="grid grid-cols-3 gap-[9px]">
            {images.map((img, index) => (
              <div key={index} className="relative w-full pt-[100%]">
                {batchMode ? (
                  /* 批量模式：点击图片区域触发勾选 */
                  <div
                    className={`absolute inset-0 rounded-[10px] overflow-hidden cursor-pointer`}
                    onClick={() => handleSelectChange(index)}
                  >
                    <img
                      src={img}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                    {/* 选中态边框 */}
                    {batchSelectIdxs.includes(index) && (
                      <div
                        className="absolute inset-0 rounded-[10px] border-2"
                        style={{ borderColor: BRAND }}
                      />
                    )}
                    {/* checkbox */}
                    <div className="absolute left-[7px] top-[7px] z-10 pointer-events-none">
                      <div
                        className={`w-[18px] h-[18px] rounded flex items-center justify-center border-2 ${
                          batchSelectIdxs.includes(index)
                            ? "border-white bg-white"
                            : "border-gray-400 bg-transparent"
                        }`}
                        style={{
                          borderColor: batchSelectIdxs.includes(index)
                            ? undefined
                            : "#999",
                        }}
                      >
                        {batchSelectIdxs.includes(index) && (
                          <i
                            className="ri-check-line text-[10px]"
                            style={{ color: BRAND }}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  /* 普通模式 */
                  <label className="absolute inset-0 rounded-[10px] overflow-hidden cursor-pointer">
                    <img
                      src={img}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </label>
                )}
              </div>
            ))}
          </div>
        )}

        {/* 空状态 */}
        {images.length === 0 && (
          <div className="pt-[60px] text-center">
            <i className="ri-image-line text-5xl block mx-auto mb-3 text-gray-200" />
            <p className="text-sm text-gray-400">暂无相册图片</p>
          </div>
        )}
      </div>

      {/* 底部操作栏 */}
      <div
        className="fixed left-0 right-0 z-40 px-4 pt-2 pb-[env(safe-area-inset-bottom)] bg-gradient-to-t from-gray-50 via-gray-50/95 to-transparent"
        style={{ bottom: 20 }}
      >
        {batchMode ? (
          /* 批量模式 */
          <div className="flex items-center justify-between h-[44px]">
            <label
              className="flex items-center gap-2 cursor-pointer"
              onClick={(e) => {
                e.preventDefault();
                toggleAllCheck();
              }}
            >
              <div
                className={`w-[18px] h-[18px] rounded flex items-center justify-center border-2 ${allCheck ? "border-white bg-white" : "border-gray-400 bg-transparent"}`}
                style={{ borderColor: allCheck ? undefined : "#999" }}
              >
                {allCheck && (
                  <i
                    className="ri-check-line text-[10px]"
                    style={{ color: BRAND }}
                  />
                )}
              </div>
              <span className="text-sm text-gray-700">全选</span>
            </label>

            <div className="flex items-center h-full gap-2.5">
              <button
                onClick={cancelBatchSelect}
                className={`h-full w-[120px] rounded-full flex items-center justify-center text-sm cursor-pointer active:opacity-70`}
                style={{
                  color: BRAND,
                  border: `1px solid ${BRAND}`,
                  background: "transparent",
                }}
              >
                取消
              </button>
              <button
                onClick={handleBatchDelete}
                disabled={batchSelectIdxs.length === 0}
                className={`h-full w-[120px] rounded-full flex flex-col items-center justify-center text-white text-sm font-medium cursor-pointer active:opacity-70 disabled:opacity-50 transition-opacity`}
                style={{ background: BRAND }}
              >
                确认删除({batchSelectIdxs.length})
              </button>
            </div>
          </div>
        ) : (
          /* 普通模式上传 — 按钮 + ref.click() 触发（与 ImageUploader 组件一致） */
          <button
            type="button"
            onClick={() => !uploading && fileInputRef.current?.click()}
            disabled={uploading}
            className={`w-full rounded-2xl text-sm font-medium flex items-center justify-center h-[44px] cursor-pointer active:scale-[0.98] transition-all ${uploading ? "opacity-60" : ""}`}
            style={{
              background: `linear-gradient(135deg, ${BRAND}, #ff8c5a)`,
              color: "#fff",
            }}
          >
            {uploading ? (
              <span className="flex items-center gap-2">
                <i className="ri-loader-4-line animate-spin" /> 上传中...
              </span>
            ) : (
              `上传图片至相册${images.length > 0 ? ` (${images.length}/${MAX_ALBUM})` : ""}`
            )}
          </button>
        )}
      </div>
    </div>
  );
}
